import { useState } from 'react';
import { toast } from 'sonner';
import { invalidateFinancials } from '../../lib/invalidate';
import { type RouterOutputs, trpc } from '../../lib/trpc';

export type InboxItem = RouterOutputs['transaction']['inbox'][number];

/**
 * Sorting new purchases: categorize, mark as savings or transfer, or exclude. Handled
 * rows are hidden right away and put back with an error toast if saving fails.
 */
export function useInboxActions(limit = 100) {
  const utils = trpc.useUtils();
  const inboxQuery = trpc.transaction.inbox.useQuery({ limit });
  // Rows handled in this session disappear immediately, before refetch
  const [done, setDone] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const resolve = trpc.sync.resolve.useMutation();
  const setTransfer = trpc.transaction.setTransferFlag.useMutation();
  const setExcluded = trpc.transaction.setExcluded.useMutation();

  const markDone = (id: string) => setDone((prev) => new Set(prev).add(id));
  const unmarkDone = (id: string) =>
    setDone((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  const refresh = () => void invalidateFinancials(utils);

  const act = async (
    item: InboxItem,
    run: () => Promise<unknown>,
    options: { refresh?: boolean } = {},
  ) => {
    markDone(item.id);
    try {
      await run();
      return true;
    } catch (err) {
      unmarkDone(item.id);
      toast.error(err instanceof Error ? err.message : 'Kunde inte spara');
      return false;
    } finally {
      if (options.refresh ?? true) refresh();
    }
  };

  const categorize = async (
    item: InboxItem,
    categoryId: string,
    createRule: boolean,
    options: { refresh?: boolean } = {},
  ) => {
    const ok = await act(
      item,
      () =>
        resolve.mutateAsync({
          transactionId: item.id,
          categoryId,
          createRule: createRule && item.rulePattern.length > 0,
          rulePattern: item.rulePattern,
        }),
      options,
    );
    if (ok && createRule) toast.success('Sorterad – liknande köp sorteras automatiskt framöver');
    return ok;
  };

  /** Put the money in a savings pot. Sorted by hand: never a rule. */
  const asSavings = async (item: InboxItem, pot: { id: string; name: string }) => {
    const ok = await act(item, () =>
      resolve.mutateAsync({ transactionId: item.id, categoryId: pot.id, createRule: false }),
    );
    if (ok) toast.success(`Sparat i ${pot.name}`);
  };

  const asTransfer = async (item: InboxItem) => {
    const ok = await act(item, () =>
      setTransfer.mutateAsync({ transactionId: item.id, isTransfer: true }),
    );
    if (ok) toast.success('Markerad som överföring mellan egna konton');
  };

  const exclude = async (item: InboxItem) => {
    const ok = await act(item, () =>
      setExcluded.mutateAsync({ transactionId: item.id, isExcluded: true }),
    );
    if (ok) toast.success('Räknas inte med i budgeten');
  };

  const items = (inboxQuery.data ?? []).filter((i) => !done.has(i.id));
  const withSuggestion = items.filter((i) => i.suggestion);

  const acceptAll = async () => {
    setBulkRunning(true);
    let accepted = 0;
    for (const item of withSuggestion) {
      if (item.suggestion) {
        // One refresh at the end instead of one per row
        if (await categorize(item, item.suggestion.categoryId, false, { refresh: false })) {
          accepted += 1;
        }
      }
    }
    setBulkRunning(false);
    refresh();
    if (accepted > 0) toast.success(`${accepted} förslag godkända`);
  };

  return {
    inboxQuery,
    items,
    withSuggestion,
    bulkRunning,
    categorize,
    asSavings,
    asTransfer,
    exclude,
    acceptAll,
  };
}
